import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import GlobalChatBar from '@/components/GlobalChatBar';
import GlobalSearch from '@/components/GlobalSearch';
import NotificationBell from '@/components/NotificationBell';
import { CaptureModalProvider, useCaptureModal } from '@/contexts/CaptureModalContext';
import { TaskDrawerProvider } from '@/contexts/TaskDrawerContext';
import { toast } from 'sonner';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { teamsApi, recallApi } from '@/utils/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import TemplatesManager from '@/components/TemplatesManager';
import '@/styles/milk-app.css';
import '@/styles/milk-v2.css';

const WORDMARK_BLUE = '/assets/logos/wordmark-blue.png';

const Ico = ({ children, fill = 'none', sw = 2 }) => (
  <svg viewBox="0 0 24 24" fill={fill} stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);

const ICONS = {
  home: <><path d="M3 11.5 12 4l9 7.5" /><path d="M5 10v10h14V10" /></>,
  workspaces: <><rect x="3" y="3" width="8" height="8" rx="2" /><rect x="13" y="3" width="8" height="8" rx="2" /><rect x="3" y="13" width="8" height="8" rx="2" /><rect x="13" y="13" width="8" height="8" rx="2" /></>,
  cards: <><rect x="3" y="5" width="18" height="14" rx="2.5" /><line x1="3" y1="10" x2="21" y2="10" /></>,
  drafts: <><path d="M4 4h11l5 5v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" /><path d="M14 4v6h6" /></>,
  tasks: <><path d="M9 11l3 3 8-8" /><path d="M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9" /></>,
  meetings: <><rect x="2" y="6" width="13" height="12" rx="2.5" /><path d="m22 8.5-5 3.5 5 3.5z" /></>,
  shared: <><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.6" y1="10.6" x2="15.4" y2="6.4" /><line x1="8.6" y1="13.4" x2="15.4" y2="17.6" /></>,
  people: <><circle cx="9" cy="8" r="3.2" /><path d="M3 20a6 6 0 0 1 12 0" /><path d="M16 5.5a3 3 0 0 1 0 5.4" /><path d="M18 20a6 6 0 0 0-3-5.2" /></>,
  you: <><circle cx="12" cy="8" r="3.4" /><path d="M5 20a7 7 0 0 1 14 0" /></>,
  search: <><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.5" y2="16.5" /></>,
  bell: <><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></>,
  plus: <><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>,
  settings: <><circle cx="12" cy="12" r="3.2" /><path d="M19.4 13a7.8 7.8 0 0 0 0-2l1.7-1.3-1.7-3-2 .8a7.6 7.6 0 0 0-1.8-1l-.3-2.1h-3.4l-.3 2.1c-.6.25-1.2.6-1.8 1l-2-.8-1.7 3L5.6 11a7.8 7.8 0 0 0 0 2l-1.7 1.3 1.7 3 2-.8c.5.4 1.1.75 1.8 1l.3 2.1h3.4l.3-2.1c.6-.25 1.2-.6 1.8-1l2 .8 1.7-3z" /></>,
  help: <><circle cx="12" cy="12" r="9" /><path d="M9.5 9a2.5 2.5 0 0 1 4.9.7c0 1.7-2.4 2.3-2.4 2.3" /><line x1="12" y1="17" x2="12" y2="17" /></>,
  logout: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5" /><line x1="21" y1="12" x2="9" y2="12" /></>,
  bolt: <polygon points="13 2 4 14 11 14 11 22 20 10 13 10 13 2" fill="currentColor" stroke="none" />,
  chev: <path d="m6 9 6 6 6-6" />,
  files: <><path d="M3 7a2 2 0 0 1 2-2h4l2 2.5h8a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></>,
  templates: <><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></>,
  workspace: <><circle cx="9" cy="7" r="3" /><circle cx="17" cy="9" r="2.4" /><path d="M3 20a6 6 0 0 1 12 0" /><path d="M15.5 20a5 5 0 0 1 6-4.8" /></>,
  teams: <><rect x="3" y="4" width="7" height="7" rx="1.5" /><rect x="14" y="4" width="7" height="7" rx="1.5" /><rect x="8.5" y="14" width="7" height="7" rx="1.5" /><path d="M6.5 11v1.5a1.5 1.5 0 0 0 1.5 1.5h1" /><path d="M17.5 11v1.5a1.5 1.5 0 0 1-1.5 1.5h-1" /></>,
};

const NAV = [
  { to: '/', label: 'Home', icon: 'home', exact: true },
  { to: '/workspaces', label: 'Workspaces', icon: 'workspaces' },
  { to: '/action-cards', label: 'Action Cards', icon: 'cards' },
  { to: '/tasks', label: 'Tasks', icon: 'tasks' },
];

// Org-level nav, pinned to the bottom near the user account.
const SECONDARY_NAV = [
  { to: '/teams', label: 'Teams', icon: 'teams' },
  { to: '/contacts', label: 'Contacts', icon: 'people' },
];

const TABS = [
  { to: '/', label: 'Home', icon: 'home', exact: true },
  { to: '/action-cards', label: 'Cards', icon: 'cards' },
  { to: '/capture', label: 'cap' },
  { to: '/tasks', label: 'Tasks', icon: 'tasks' },
  { to: '/settings', label: 'You', icon: 'you' },
];

function initialsOf(name) {
  return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
}

function Sidebar({ user, onSearch, onCapture, onAddMeeting, onOpenTemplates, onOpenShortcuts, onLogout, onNav, inviteCount }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [captureMenuOpen, setCaptureMenuOpen] = useState(false);
  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [menuOpen]);
  useEffect(() => {
    if (!captureMenuOpen) return;
    const close = () => setCaptureMenuOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [captureMenuOpen]);

  return (
    <aside className="sidebar">
      <div className="sb-logo" style={{ justifyContent: 'space-between' }}>
        <img src={WORDMARK_BLUE} alt="Milk" />
        <NotificationBell />
      </div>
      <button className="sb-search" onClick={onSearch} data-testid="sidebar-search-button">
        <Ico>{ICONS.search}</Ico>Search<span className="kbd">/</span>
      </button>
      <div className="capture-wrap" style={{ position: 'relative' }}>
        <div className="cap-row">
          <button className="btn-capture" onClick={onCapture} data-testid="sidebar-new-capture-button">
            <Ico fill="currentColor" sw={0}>{ICONS.bolt}</Ico>Capture</button>
          <button
            className="cap-dd"
            aria-label="More capture options"
            data-testid="sidebar-capture-more-button"
            onClick={(e) => { e.stopPropagation(); setCaptureMenuOpen(o => !o); }}
          >
            <Ico>{ICONS.chev}</Ico>
          </button>
        </div>
        {captureMenuOpen && (
          <div className="capture-menu" data-testid="sidebar-capture-menu" onClick={(e) => e.stopPropagation()}>
            <button data-testid="capture-menu-record" onClick={() => { setCaptureMenuOpen(false); onCapture(); }}>
              <Ico fill="currentColor" sw={0}>{ICONS.bolt}</Ico>
              <span><span className="cm-t">Record a capture</span><span className="cm-s">Talk, upload or paste notes</span></span>
            </button>
            <button data-testid="capture-menu-meeting" onClick={() => { setCaptureMenuOpen(false); onAddMeeting(); }}>
              <Ico>{ICONS.meetings}</Ico>
              <span><span className="cm-t">Add meeting link</span><span className="cm-s">Send a notetaker to a video call</span></span>
            </button>
          </div>
        )}
      </div>
      <nav className="sb-nav">
        {NAV.map(item => (
          <NavLink key={item.to} to={item.to} end={item.exact} className={({ isActive }) => isActive ? 'active' : ''} data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/ /g, '-')}`}>
            <Ico>{ICONS[item.icon]}</Ico>{item.label}
            {item.label === 'Workspace' && inviteCount > 0 && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onNav('/workspace#invitations'); }}
                title={`${inviteCount} pending invitation${inviteCount === 1 ? '' : 's'}`}
                data-testid="sidebar-workspace-invite-badge"
                style={{
                  marginLeft: 'auto',
                  minWidth: 18,
                  height: 18,
                  padding: '0 5px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  fontWeight: 700,
                  lineHeight: 1,
                  color: '#fff',
                  background: '#d4a44a',
                  borderRadius: 9,
                  cursor: 'pointer',
                }}
              >
                {inviteCount}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="sb-spacer" />
      <nav className="sb-nav sb-nav-secondary">
        {SECONDARY_NAV.map(item => (
          <NavLink key={item.to} to={item.to} className={({ isActive }) => isActive ? 'active' : ''} data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/ /g, '-')}`}>
            <Ico>{ICONS[item.icon]}</Ico>{item.label}
          </NavLink>
        ))}
      </nav>
      <div className={`sb-account${menuOpen ? ' open' : ''}`}>
        <div className="sb-menu">
          <button onClick={() => { setMenuOpen(false); onNav('/settings'); }}><Ico>{ICONS.settings}</Ico>Settings</button>
          <button onClick={() => { setMenuOpen(false); onOpenTemplates(); }} data-testid="account-menu-templates"><Ico>{ICONS.templates}</Ico>Templates</button>
          <button onClick={() => { setMenuOpen(false); onNav('/files'); }} data-testid="account-menu-files"><Ico>{ICONS.files}</Ico>Files</button>
          <button onClick={() => { setMenuOpen(false); toast.info('Help & support — contact us anytime.'); }}><Ico>{ICONS.help}</Ico>Help &amp; support</button>
          <button onClick={() => { setMenuOpen(false); onOpenShortcuts(); }} data-testid="account-menu-shortcuts"><Ico>{ICONS.help}</Ico>Keyboard shortcuts</button>
          <div className="sb-menu-div" />
          <button onClick={onLogout} data-testid="sidebar-logout-button"><Ico>{ICONS.logout}</Ico>Sign out</button>
        </div>
        <button className="sb-user" onClick={(e) => { e.stopPropagation(); setMenuOpen(o => !o); }} data-testid="sidebar-account-button">
          <div className="av">{initialsOf(user?.name)}</div>
          <div className="u-txt"><div className="nm">{user?.name}</div><div className="em">{user?.email}</div></div>
          <svg className="u-chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{ICONS.chev}</svg>
        </button>
      </div>
    </aside>
  );
}

export default function AppShell() {
  return (
    <CaptureModalProvider>
      <AppShellInner />
    </CaptureModalProvider>
  );
}

function ReconnectBanner() {
  const { user, connectGoogle, connectMicrosoft } = useAuth();
  const health = user?.connection_health || {};
  const provider = health.google === 'needs_reconnect' ? 'google'
    : health.microsoft === 'needs_reconnect' ? 'microsoft' : null;
  if (!provider) return null;
  const pname = provider === 'microsoft' ? 'Microsoft' : 'Google';
  const reconnect = provider === 'microsoft' ? connectMicrosoft : connectGoogle;
  return (
    <div
      data-testid="reconnect-banner"
      className="flex items-center justify-center gap-3 px-4 py-2 text-xs sm:text-sm"
      style={{ background: '#fef3c7', borderBottom: '1px solid #fcd34d', color: '#92400e' }}
    >
      <AlertTriangle size={15} className="shrink-0" />
      <span className="font-medium">Your {pname} connection needs attention — reconnect to keep sending follow-ups & scheduling.</span>
      <button
        onClick={reconnect}
        data-testid="reconnect-banner-button"
        className="shrink-0 inline-flex items-center gap-1 font-semibold px-2.5 h-7 rounded-md text-white transition-colors"
        style={{ background: '#d4a44a' }}
      >
        Reconnect {pname}
      </button>
    </div>
  );
}

function AppShellInner() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { openModal } = useCaptureModal();
  const [searchOpen, setSearchOpen] = useState(false);
  const [inviteCount, setInviteCount] = useState(0);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [meetingUrl, setMeetingUrl] = useState('');
  const [sendingBot, setSendingBot] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const hideChatBar = location.pathname === '/capture' || location.pathname === '/settings';

  useEffect(() => {
    let active = true;
    const load = () => {
      teamsApi.invitations()
        .then(res => { if (active) setInviteCount((res.data?.invitations || []).length); })
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 60000);
    return () => { active = false; clearInterval(id); };
  }, [location.pathname]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setSearchOpen(p => !p); return; }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement;
      const tag = el?.tagName?.toLowerCase();
      const typing = tag === 'input' || tag === 'textarea' || tag === 'select' || el?.isContentEditable;
      if (typing) return;
      if (e.key === '?') { e.preventDefault(); setShortcutsOpen(o => !o); return; }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleLogout = async () => { await logout(); navigate('/login'); };
  const goCapture = () => openModal();

  const sendMeetingBot = async () => {
    const url = meetingUrl.trim();
    if (!/^https?:\/\//i.test(url)) {
      toast.error('Enter a valid meeting link (Zoom, Google Meet, or Teams).');
      return;
    }
    setSendingBot(true);
    try {
      await recallApi.send({ meeting_url: url });
      toast.success('Notetaker is on its way to the meeting');
      setMeetingUrl('');
      setMeetingOpen(false);
      navigate('/meetings');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send the notetaker');
    } finally {
      setSendingBot(false);
    }
  };

  return (
    <div className="mlk-app">
      <TaskDrawerProvider>
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      <div className="mlk-shell">
        <Sidebar
          user={user}
          onSearch={() => setSearchOpen(true)}
          onCapture={goCapture}
          onAddMeeting={() => { setMeetingUrl(''); setMeetingOpen(true); }}
          onOpenTemplates={() => setTemplatesOpen(true)}
          onOpenShortcuts={() => setShortcutsOpen(true)}
          onLogout={handleLogout}
          onNav={navigate}
          inviteCount={inviteCount}
        />

        <div className="main">
          {/* Mobile top bar */}
          <header className="topbar">
            <div className="av" style={{ width: 32, height: 32, fontSize: 12 }}>{initialsOf(user?.name)}</div>
            <img className="tb-logo" src={WORDMARK_BLUE} alt="Milk" />
            <div className="tb-right">
              <button className="icbtn" aria-label="Search" onClick={() => setSearchOpen(true)} data-testid="topbar-search-button"><Ico>{ICONS.search}</Ico></button>
              <NotificationBell />
            </div>
          </header>

          <ReconnectBanner />

          <div className="scroll">
            <Outlet />
          </div>

          {/* Desktop Ask-Anything chat bar (kept per requirement) — floats over content */}
          {!hideChatBar && (
            <div
              className="mlk-chatbar hidden md:flex"
              style={{ position: 'absolute', bottom: 0, left: 0, right: 0, justifyContent: 'center', padding: '8px 12px 22px', background: 'transparent', pointerEvents: 'none', zIndex: 20 }}
            >
              <GlobalChatBar />
            </div>
          )}

          {/* Mobile bottom tab bar */}
          <nav className="tabbar">
            {TABS.map(tab => tab.label === 'cap' ? (
              <button key="cap" className="tab cap" aria-label="Capture" onClick={goCapture} data-testid="tabbar-capture-button">
                <span className="capb"><Ico sw={2.4}>{ICONS.plus}</Ico></span>
              </button>
            ) : (
              <NavLink key={tab.to} to={tab.to} end={tab.exact} className={({ isActive }) => `tab${isActive ? ' active' : ''}`} data-testid={`tabbar-${tab.label.toLowerCase()}`}>
                <Ico>{ICONS[tab.icon]}</Ico>{tab.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>
      <TemplatesManager open={templatesOpen} onOpenChange={setTemplatesOpen} />

      <Dialog open={shortcutsOpen} onOpenChange={setShortcutsOpen}>
        <DialogContent data-testid="shortcuts-dialog">
          <DialogHeader>
            <DialogTitle>Keyboard shortcuts</DialogTitle>
            <DialogDescription>Move faster around Milk without touching the mouse.</DialogDescription>
          </DialogHeader>
          <div className="mt-1 divide-y" style={{ borderColor: 'rgba(33,46,64,.08)' }}>
            {[
              ['Open search', ['/']],
              ['Command search', ['⌘', 'K']],
              ['Show this help', ['?']],
            ].map(([label, keys]) => (
              <div key={label} className="flex items-center justify-between py-2.5" data-testid={`shortcut-row-${label.toLowerCase().replace(/ /g, '-')}`}>
                <span className="text-sm" style={{ color: '#212E40' }}>{label}</span>
                <span className="flex items-center gap-1">
                  {keys.map((k, i) => (
                    <kbd key={i} className="min-w-[26px] text-center text-xs font-semibold px-2 py-1 rounded-md"
                      style={{ background: '#FBFAF6', border: '1px solid rgba(33,46,64,.16)', color: '#212E40' }}>{k}</kbd>
                  ))}
                </span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={meetingOpen} onOpenChange={(o) => { if (!sendingBot) setMeetingOpen(o); }}>
        <DialogContent data-testid="add-meeting-dialog">
          <DialogHeader>
            <DialogTitle>Capture a meeting</DialogTitle>
            <DialogDescription>
              Paste a Zoom, Google Meet or Teams link. Milk sends a notetaker that joins, records, and turns the
              conversation into an Action Card.
            </DialogDescription>
          </DialogHeader>
          <input
            data-testid="add-meeting-url-input"
            value={meetingUrl}
            onChange={(e) => setMeetingUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && meetingUrl.trim()) sendMeetingBot(); }}
            placeholder="https://zoom.us/j/…"
            autoFocus
            className="w-full h-11 px-3.5 rounded-xl text-sm outline-none border"
            style={{ background: '#FBFAF6', borderColor: 'rgba(33,46,64,.14)', color: '#212E40' }}
          />
          <DialogFooter>
            <button
              onClick={() => setMeetingOpen(false)}
              disabled={sendingBot}
              data-testid="add-meeting-cancel-button"
              className="inline-flex items-center justify-center text-sm font-medium h-10 px-4 rounded-xl border border-border bg-white hover:bg-secondary/40 transition-colors disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              onClick={sendMeetingBot}
              disabled={sendingBot || !meetingUrl.trim()}
              data-testid="add-meeting-send-button"
              className="inline-flex items-center justify-center gap-2 text-sm font-semibold h-10 px-4 rounded-xl text-white transition-colors disabled:opacity-50"
              style={{ background: '#6994c6' }}
            >
              {sendingBot ? <><Loader2 size={14} className="animate-spin" />Sending…</> : 'Send notetaker'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </TaskDrawerProvider>
    </div>
  );
}
